import { useState } from 'react';
import { supabase } from '../supabase';
import { fetchTypePipelines } from '../workItemPipelines';
import { UNTITLED_WORK_ITEM_TITLE } from '../editor/defaultTitles';

export type CreatableWorkItemType = 'epic' | 'story' | 'bug' | 'task';

const NEW_WORK_ITEM_DEFAULT_STATUS = 'Backlog';

export interface UseCreateWorkItem {
  creating: boolean;
  error: string | null;
  /**
   * Create a root work item of `type`, attach it to the project, and return the
   * new work item id (or null on failure, with `error` set).
   */
  create: (
    orgId: string,
    projectId: string,
    type: CreatableWorkItemType
  ) => Promise<string | null>;
}

/**
 * "Create new work item / epic" from the Project page. Mirrors the Work Items
 * list `+ New work item` flow (default status from the per-type pipeline via
 * `create_work_item`) and then attaches the new root item to the project via
 * the audited `set_work_item_project` RPC (`useProjectMembership.setWorkItemProject`).
 */
export function useCreateWorkItem(): UseCreateWorkItem {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (
    orgId: string,
    projectId: string,
    type: CreatableWorkItemType
  ): Promise<string | null> => {
    setCreating(true);
    setError(null);
    try {
      // Default to the per-type pipeline's first stage (e.g. Epic → Proposed),
      // falling back to the shared pipeline, then the hard-coded 'Backlog'.
      const { byType, error: pipeErr } = await fetchTypePipelines(
        [type, null],
        orgId
      );
      if (pipeErr) {
        setError(pipeErr);
        return null;
      }
      const stages = byType.get(type) ?? byType.get(null);
      const firstStage =
        stages && stages.length > 0 ? stages[0] : NEW_WORK_ITEM_DEFAULT_STATUS;

      const { data, error: insErr } = await supabase.rpc('create_work_item', {
        p_org_id: orgId,
        p_type: type,
        p_name: UNTITLED_WORK_ITEM_TITLE,
        p_status: firstStage
      });
      const env = data as { ok?: boolean; work_item?: { id?: string } } | null;
      if (insErr || !env?.ok || !env.work_item?.id) {
        setError(
          insErr?.message ?? "Couldn't create the work item. Please try again."
        );
        return null;
      }
      const newId = env.work_item.id;

      const { error: linkErr } = await supabase.rpc('set_work_item_project', {
        p_work_item_id: newId,
        p_project_id: projectId
      });
      if (linkErr) {
        setError(linkErr.message);
        return null;
      }
      return newId;
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unexpected error creating work item'
      );
      return null;
    } finally {
      setCreating(false);
    }
  };

  return { creating, error, create };
}
